/*
 * Quick add "+" button on product cards (collection pages and homepage).
 * - Single variant products: added straight away.
 * - Other products: open <quick-add-picker> (theme Drawer).
 *   Desktop: a strip over the bottom of the card image; clicking a size adds it.
 *   Mobile: a bottom sheet; tap a size, then "Add to cart".
 * After adding, the cart is refreshed the same way the product form does it
 * (Slide Cart when cart type is "page", otherwise the theme "product:added" event).
 */
if (!customElements.get('quick-add-picker')) {
  const desktopQuery = window.matchMedia('screen and (min-width: 768px)');

  const quickAddToCart = (variantId) => {
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    return fetch(window.routes.cartAddUrl + '.js', {
      body: JSON.stringify({ id: variantId, quantity: 1 }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).then((response) => {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));

      return response.json().then((content) => {
        if (!response.ok) {
          const message = typeof content['description'] === 'string' ? content['description'] : content['message'];
          throw new Error(message || '');
        }
        return content;
      });
    }, (error) => {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      throw error;
    });
  };

  const syncCartCount = () => {
    return fetch(window.routes.cartUrl + '.js', { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((cart) => {
        document.querySelectorAll('.CartCountBubble').forEach((bubble) => {
          bubble.classList.toggle('Visible', cart.item_count > 0);
        });
        document.querySelectorAll('.CartCountBubble__Count[aria-hidden="true"]').forEach((count) => {
          count.textContent = cart.item_count;
        });
      });
  };

  const refreshCart = (sourceElement, lineItem) => {
    if (window.theme.cartType == 'page') {
      if (window.SLIDECART_STATE && typeof window.SLIDECART_UPDATE === 'function' && typeof window.SLIDECART_OPEN === 'function') {
        window.SLIDECART_UPDATE();
        window.SLIDECART_OPEN();
      } else {
        // Stay on the collection page; only update the header count.
        syncCartCount();
      }
    } else {
      sourceElement.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: {
          variant: null,
          quantity: lineItem.quantity,
          cartItemKey: lineItem.key
        }
      }));
    }
  };

  class TG_QuickAddPicker extends HTMLElement {
    connectedCallback() {
      this.drawer = themegoal.components.Drawer.getOrCreateInstance(this);
      this.optionsElement = this.querySelector('[data-quick-add-options]');
      this.errorElement = this.querySelector('[data-quick-add-error]');
      this.submitButton = this.querySelector('[data-quick-add-submit]');
      this.statusElement = this.parentElement.querySelector('[data-quick-add-status]');
      this.trigger = null;
      this.product = null;
      this.selected = [];

      this.onOptionClick = this._onOptionClick.bind(this);
      this.onShown = this._onShown.bind(this);
      this.onHidden = this._onHidden.bind(this);
      this.onViewportChange = () => this.close();

      this.addEventListener('click', this.onOptionClick);
      this.addEventListener('shown.tg.Drawer', this.onShown);
      this.addEventListener('hidden.tg.Drawer', this.onHidden);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onOptionClick);
      this.removeEventListener('shown.tg.Drawer', this.onShown);
      this.removeEventListener('hidden.tg.Drawer', this.onHidden);
      window.removeEventListener('resize', this.onViewportChange);
      if (this.drawer) this.drawer.dispose();
    }

    open(trigger) {
      const dataElement = trigger.parentElement.querySelector('[data-quick-add-json]');
      if (!dataElement) return;

      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');

      this.trigger = trigger;
      this.product = JSON.parse(dataElement.textContent);

      // Preselect the options (size included) of the first available variant.
      const firstAvailable = this.product.variants.find((variant) => variant.available) || this.product.variants[0];
      this.selected = firstAvailable.options.slice();

      this.querySelector('[data-quick-add-product-title]').textContent = `: ${this.product.title}`;
      this._hideError();
      this._render();
      this._position();

      trigger.setAttribute('aria-expanded', 'true');
      this.drawer.show(trigger);
      window.addEventListener('resize', this.onViewportChange);
    }

    close() {
      this.drawer.hide();
    }

    announce(message) {
      if (!this.statusElement) return;
      this.statusElement.textContent = '';
      window.setTimeout(() => { this.statusElement.textContent = message; }, 50);
    }

    _render() {
      const { options, sizeIndex } = this.product;
      const soldOutLabel = this.getAttribute('data-sold-out-label');

      this.optionsElement.innerHTML = '';

      options.forEach((option, optionIndex) => {
        const isSize = optionIndex === sizeIndex;
        const group = document.createElement('div');
        group.className = 'QuickAddPicker__Group';
        group.setAttribute('role', 'group');

        if (options.length > 1) {
          const label = document.createElement('span');
          label.className = 'QuickAddPicker__GroupLabel';
          label.id = `QuickAddPickerGroup-${optionIndex}`;
          label.textContent = option.name;
          group.setAttribute('aria-labelledby', label.id);
          group.appendChild(label);
        } else {
          group.setAttribute('aria-label', option.name);
        }

        const values = document.createElement('div');
        values.className = 'QuickAddPicker__Values';

        option.values.forEach((value) => {
          const button = document.createElement('button');
          const available = this._isAvailable(optionIndex, value);

          button.type = 'button';
          button.className = 'QuickAddPicker__Value';
          button.textContent = value;
          button.dataset.optionIndex = optionIndex;
          button.dataset.value = value;

          button.setAttribute('aria-pressed', this.selected[optionIndex] === value ? 'true' : 'false');
          if (!available) button.setAttribute('aria-label', `${value}, ${soldOutLabel}`);

          if (isSize) {
            button.dataset.quickAddValue = '';
            button.disabled = !available;
          }

          values.appendChild(button);
        });

        group.appendChild(values);
        this.optionsElement.appendChild(group);
      });

      const sizeSelected = this._isAvailable(sizeIndex, this.selected[sizeIndex]);
      this.submitButton.disabled = !sizeSelected;
    }

    // After another option changes, keep the selected size if it is still available, otherwise pick the first one that is.
    _ensureAvailableSize() {
      const { options, sizeIndex } = this.product;
      if (this._isAvailable(sizeIndex, this.selected[sizeIndex])) return;

      const firstSize = options[sizeIndex].values.find((value) => this._isAvailable(sizeIndex, value));
      if (firstSize) this.selected[sizeIndex] = firstSize;
    }

    // A value is available when some available variant has it together with the
    // currently selected values of the other non-size options.
    _isAvailable(optionIndex, value) {
      const { sizeIndex } = this.product;

      return this.product.variants.some((variant) => {
        if (!variant.available || variant.options[optionIndex] !== value) return false;
        return variant.options.every((optionValue, index) => {
          return index === optionIndex || index === sizeIndex || optionValue === this.selected[index];
        });
      });
    }

    _findVariant(options) {
      return this.product.variants.find((variant) => {
        return variant.options.every((value, index) => value === options[index]);
      });
    }

    _onOptionClick(event) {
      if (event.target.closest('[data-quick-add-submit]')) {
        const variant = this._findVariant(this.selected);
        if (variant && variant.available) this._addVariant(variant, this.submitButton);
        return;
      }

      const button = event.target.closest('.QuickAddPicker__Value');
      if (!button || button.disabled) return;

      const optionIndex = parseInt(button.dataset.optionIndex, 10);
      const value = button.dataset.value;
      const isSize = button.hasAttribute('data-quick-add-value');

      // Desktop: clicking a size adds it right away.
      if (isSize && desktopQuery.matches) {
        const options = this.selected.slice();
        options[optionIndex] = value;
        const variant = this._findVariant(options);
        if (variant) this._addVariant(variant, button);
        return;
      }

      this.selected[optionIndex] = value;
      if (!isSize) this._ensureAvailableSize();
      this._hideError();
      this._render();
      const pressed = this.optionsElement.querySelector(`.QuickAddPicker__Value[data-option-index="${optionIndex}"][aria-pressed="true"]`);
      if (pressed) pressed.focus();
    }

    _addVariant(variant, button) {
      const trigger = this.trigger;

      this._hideError();
      this.querySelectorAll('.QuickAddPicker__Value, [data-quick-add-submit]').forEach((element) => {
        element.setAttribute('disabled', 'disabled');
      });
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      trigger.classList.add('is-loading');

      const done = () => {
        trigger.classList.remove('is-loading');
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        this.submitButton.removeAttribute('disabled');
      };

      quickAddToCart(variant.id).then((lineItem) => {
        done();
        this.close();
        TG_QuickAddPicker.flashAdded(trigger);
        this.announce(this.getAttribute('data-added-message'));
        refreshCart(trigger, lineItem);
      }).catch((error) => {
        done();
        this._render();
        this._showError(error.message || this.getAttribute('data-error-message'));
      });
    }

    _showError(message) {
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    _hideError() {
      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }

    // Desktop: lay the strip over the bottom of the card image, inset like the "+" button.
    _position() {
      ['top', 'left', 'width'].forEach((property) => this.style.removeProperty(property));
      if (!desktopQuery.matches) return;

      const imageWrapper = this.trigger.closest('.ProductCard__ImageWrapper') || this.trigger.parentElement;
      const rect = imageWrapper.getBoundingClientRect();
      const inset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--tg-component-quick-add-inset')) || 6;

      this.style.width = `${rect.width - inset * 2}px`;
      this.style.left = `${rect.left + inset}px`;
      this.style.top = `${rect.bottom - inset - this.offsetHeight}px`;
    }

    _onShown() {
      const target = desktopQuery.matches
        ? this.optionsElement.querySelector('[data-quick-add-value]:not(:disabled)')
        : this.optionsElement.querySelector('[data-quick-add-value][aria-pressed="true"]:not(:disabled)');
      (target || this.querySelector('.QuickAddPicker__Close')).focus();
    }

    _onHidden() {
      window.removeEventListener('resize', this.onViewportChange);
      if (!this.trigger) return;

      this.trigger.setAttribute('aria-expanded', 'false');
      if (document.body.contains(this.trigger)) this.trigger.focus({ preventScroll: true });
      this.trigger = null;
    }

    static flashAdded(trigger) {
      trigger.classList.add('is-added');
      window.setTimeout(() => trigger.classList.remove('is-added'), 1500);
    }
  }

  customElements.define('quick-add-picker', TG_QuickAddPicker);

  const showCardError = (trigger, message) => {
    const container = trigger.parentElement;
    const existing = container.querySelector('.ProductCard__QuickAddMessage');
    if (existing) existing.remove();

    const messageElement = document.createElement('p');
    messageElement.className = 'ProductCard__QuickAddMessage Alert Alert--danger Alert--small';
    messageElement.setAttribute('role', 'alert');
    messageElement.textContent = message;
    container.appendChild(messageElement);

    window.setTimeout(() => messageElement.remove(), 5000);
  };

  // Delegated on the document so cards re-rendered by filters/pagination keep working.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-quick-add]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    const picker = document.getElementById('QuickAddPicker');
    const variantId = trigger.getAttribute('data-quick-add-variant-id');

    if (!variantId) {
      if (picker) picker.open(trigger);
      return;
    }

    if (trigger.classList.contains('is-loading')) return;

    trigger.classList.add('is-loading');
    trigger.setAttribute('aria-busy', 'true');

    quickAddToCart(parseInt(variantId, 10)).then((lineItem) => {
      trigger.classList.remove('is-loading');
      trigger.removeAttribute('aria-busy');
      TG_QuickAddPicker.flashAdded(trigger);
      if (picker) picker.announce(picker.getAttribute('data-added-message'));
      refreshCart(trigger, lineItem);
    }).catch((error) => {
      trigger.classList.remove('is-loading');
      trigger.removeAttribute('aria-busy');
      showCardError(trigger, error.message || (picker && picker.getAttribute('data-error-message')) || '');
    });
  });
}
